import React from 'react'
import { Controller } from 'react-hook-form'
import FormField from './FormField'
import SearchableCheckboxGroup from './SearchableCheckboxGroup'

/**
 * A wrapper around FormField that integrates with React Hook Form's Controller.
 * Handles the connection between RHF's control and our FormField component.
 *
 * @example
 * <FormFieldController
 *   control={control}
 *   name="name"
 *   label="Name"
 *   required
 *   errors={errors}
 * />
 *
 * @example
 * <FormFieldController
 *   control={control}
 *   name="category"
 *   label="Category"
 *   type="select"
 *   options={categories}
 *   errors={errors}
 * />
 */
export default function FormFieldController({
  control,
  name,
  errors,
  defaultValue = '',
  ...fieldProps
}) {
  const error = errors?.[name]?.message

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field }) => {
        // Handle different field types
        const { type } = fieldProps

        if (type === 'checkbox') {
          return (
            <FormField
              {...fieldProps}
              name={name}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              error={error}
            />
          )
        }

        if (type === 'checkboxGroup') {
          return (
            <FormField
              {...fieldProps}
              name={name}
              value={field.value || []}
              onChange={(e) => field.onChange(e.target.value)}
              error={error}
            />
          )
        }

        if (type === 'searchableCheckboxGroup') {
          const { label, required, hint, options, maxItems, placeholder, emptyMessage } = fieldProps
          return (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <SearchableCheckboxGroup
                name={name}
                value={field.value || []}
                onChange={(e) => field.onChange(e.target.value)}
                options={options}
                maxItems={maxItems}
                placeholder={placeholder}
                emptyMessage={emptyMessage}
                error={error}
              />
              {hint && (
                <p className="mt-1 text-sm text-gray-500">{hint}</p>
              )}
            </div>
          )
        }

        if (type === 'file') {
          return (
            <FormField
              {...fieldProps}
              name={name}
              onChange={(file) => field.onChange(file)}
              error={error}
            />
          )
        }

        if (type === 'currency') {
          return (
            <FormField
              {...fieldProps}
              name={name}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              error={error}
            />
          )
        }

        // Default: text, textarea, select, email, url, number, etc.
        return (
          <FormField
            {...fieldProps}
            name={name}
            value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
            error={error}
          />
        )
      }}
    />
  )
}
